/* línkè · 每个主题各记各的外观（Theme Skin）
   2026-07-28 铃点单：她在浅色主题下调好的浅粉壁纸、字色、气泡色，
   切到夜空就全不协调——因为这些设置本来是「一份通用」，三个主题共用。
   现在改成：夜空 / 日落 / 玫瑰 / 初雪 各自记住自己的一整套，切过去自动换上，切回来原样还在。

   ── 存储约定（重要，改这里前先读懂）──
   「当前主题」的设置**仍然住在原来的 key 里**（lingke.bg / lingke.accent / …），
   所以 bg.js、accent.js、wallpaper.js 等模块一行都不用改，照常读写。
   只有「其它主题」的那几套被寄存在带后缀的 key 里：`原key@主题id`。
   切主题时做一次交接：把当前这套存进旧主题名下 → 把新主题那套搬回原 key。
   好处=模块无感、逻辑只有这一处；代价=切主题时多几次 localStorage 读写（无网络、无同步，
   这些 key 不在 sync.js 的 SYNCED_KEYS 白名单里）。

   ── 壁纸图片不参与「继承」──
   壁纸是整张图（data URL，可能几百 KB），复制三份容易撑爆 localStorage 配额；
   而且「浅粉壁纸跟去夜空」正是她要解决的问题本身。所以首次分家时
   图片只留在她当前那个主题，另外两个主题从「没有壁纸」开始，她各自挑一张。 */

const THEME_KEY = 'lingke.theme';
const THEME_IDS = ['dark', 'light', 'rose', 'snow'];
/* sync.js 也要引：云端恢复完成的新设备直接立此标记=跳过「首次分家」搬家（见 ensureSeeded） */
export const SEEDED_FLAG = 'lingke.skin.seeded.v1';

/* 会跟着主题走的设置。图片单列（见文件头：不参与继承）。 */
const SKIN_KEYS = [
  'lingke.bg',
  'lingke.textColor',
  'lingke.accent',
  'lingke.userBubble',
  'lingke.bubbleSolid',
  'lingke.chat.wallpaper.dim.v1',
  'lingke.chat.wallpaper.blur.v1',
  'lingke.chat.wallpaper.pos.v1',
  'lingke.chat.wallpaper.top.v1',
  'lingke.chat.wallpaper.toprgb.v1',
  'lingke.home.wallpaper.dim.v1',
  'lingke.home.wallpaper.blur.v1',
  'lingke.home.wallpaper.pos.v1',
];
const IMG_KEYS = [
  'lingke.chat.wallpaper.img.v1',
  'lingke.home.wallpaper.img.v1',
];
const TRACKED_KEYS = [...SKIN_KEYS, ...IMG_KEYS];

/* 供 sync.js 用：四个主题名下寄存的「柜子 key」全集（`原key@主题id`）。
   2026-07-30 教训：云同步曾只同步「身上穿的」（原 key）而不同步柜子，
   清缓存恢复后柜子全空、seeded 标记却被同步回来 → ensureSeeded 不再分家 →
   切主题没得换，一套粉色行头跟着串遍四个主题。柜子必须一起备份。 */
export const SCOPED_SYNC_KEYS = TRACKED_KEYS.flatMap((k) =>
  THEME_IDS.map((id) => scopedKey(k, id)),
);

/* 供 sync.js 用：跟着主题走的「身上穿的」原 key 全集。
   这些 key 的备份真身在柜子(@主题)里，所以「身上穿的」在服务器上可以放心
   跟当前设备如实镜像——本地空(=跟随主题)就把服务器旧值清掉，防止清缓存/新增
   主屏幕 App 时把早已过时的旧配饰穿回来（2026-07-30 铃「蓝主题披粉配饰」案）。 */
export const THEME_MANAGED_KEYS = [...TRACKED_KEYS];

/* 切主题后广播一声：Chat / SideDrawer 里用 useState 存着的壁纸要重读，
   否则 localStorage 变了、界面还是旧图。 */
export const SKIN_EVENT = 'lingke:skin';

function scopedKey(key, themeId) {
  return `${key}@${themeId}`;
}

export function currentThemeId() {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return THEME_IDS.includes(v) ? v : 'dark';
  } catch {
    return 'dark';
  }
}

/* 把「现在生效的这一套」拍进某个主题名下（离开该主题时调用）。
   配额满了就静默跳过：宁可这次没存上，也不能让切主题整个崩掉。 */
function snapshotTheme(themeId) {
  for (const k of TRACKED_KEYS) {
    try {
      const v = localStorage.getItem(k);
      if (v === null) localStorage.removeItem(scopedKey(k, themeId));
      else localStorage.setItem(scopedKey(k, themeId), v);
    } catch {}
  }
}

/* 把某个主题名下那一套搬回原 key（进入该主题时调用）。
   该主题没存过的项 → 清掉原 key，让模块回落到主题默认值（例如新主题一开始没有壁纸）。 */
function restoreTheme(themeId) {
  for (const k of TRACKED_KEYS) {
    try {
      const v = localStorage.getItem(scopedKey(k, themeId));
      if (v === null) localStorage.removeItem(k);
      else localStorage.setItem(k, v);
      /* 搬回来之后删掉寄存副本：当前主题的设置只住在原 key 一处，
         避免同一张壁纸在 localStorage 里存两份白占配额。 */
      localStorage.removeItem(scopedKey(k, themeId));
    } catch {}
  }
}

/* 首次分家：把她现在调好的这套颜色**复制给另外两个主题**，
   这样上线当天切过去不会突然变回出厂色、她原来的心血一点不丢。
   （壁纸图片不复制，理由见文件头。） */
export function ensureSeeded() {
  try {
    if (localStorage.getItem(SEEDED_FLAG)) return;
  } catch {
    return;
  }
  const cur = currentThemeId();
  for (const id of THEME_IDS) {
    if (id === cur) continue; // 当前主题住在原 key，不需要副本
    for (const k of SKIN_KEYS) {
      try {
        /* 只补空柜、绝不覆盖：柜子里已有的（比如刚被云同步恢复回来的那套）
           是各主题自己的行头，分家只给「还没行头」的主题发起点副本（2026-07-30）。 */
        if (localStorage.getItem(scopedKey(k, id)) !== null) continue;
        const v = localStorage.getItem(k);
        if (v !== null) localStorage.setItem(scopedKey(k, id), v);
      } catch {}
    }
  }
  try {
    localStorage.setItem(SEEDED_FLAG, '1');
  } catch {}
}

/* 主题交接：旧主题收好 → 新主题铺开。theme.js 的 useTheme 在切换时调用。 */
export function handoffTheme(fromId, toId) {
  if (!fromId || fromId === toId) return;
  snapshotTheme(fromId);
  restoreTheme(toId);
}

/* 广播「外观已换」——组件里 useState 缓存的壁纸据此重读。 */
export function notifySkinChanged() {
  try {
    window.dispatchEvent(new CustomEvent(SKIN_EVENT));
  } catch {}
}
